import { useState } from 'react';
import Typography from '@mui/material/Typography';
import { FormattedMessage } from 'react-intl';
import { useLocalization } from '@ui-framework/index';
import { Button, Card, Divider, IconButton, Slide, Stack } from '@mui/material';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import HighlightOffRoundedIcon from '@mui/icons-material/HighlightOffRounded';
import BlockIcon from '@mui/icons-material/Block';
import PushPinIcon from '@mui/icons-material/PushPin';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import StarIcon from '@mui/icons-material/Star';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PhoneIcon from '@mui/icons-material/Phone';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ClearIcon from '@mui/icons-material/Clear';
import KeyboardDoubleArrowUpIcon from '@mui/icons-material/KeyboardDoubleArrowUp';
import ServiceIcon from './ServiceIcon';
import LegendBlock from './LegendBlock';
import { QUADRANT_GAP } from '../../markers/ServiceProvider';

export default function Legend() {
  const [open, setOpen] = useState(false);
  const { formatMessage } = useLocalization();

  return (
    <div>
      <Button
        variant="contained"
        sx={{
          position: 'absolute',
          bottom: '38px',
          right: '100px',
          zIndex: 2,
          textTransform: 'none',
          padding: '2px 6px',
          fontSize: '12px',
          backgroundColor: 'background.paper',
          color: 'text.primary',
          ':hover': {
            backgroundColor: 'neutral.default',
          },
        }}
        onClick={() => setOpen(true)}>
        <FormattedMessage id="SERVICEJOB_SHOW_LEGEND" defaultMessage="Legend" />
        <KeyboardDoubleArrowUpIcon
          fontSize="small"
          sx={{ marginLeft: '5px' }}
        />
      </Button>
      <Slide direction="up" in={open} mountOnEnter unmountOnExit>
        <Card
          elevation={8}
          sx={{
            position: 'absolute',
            bottom: '10px',
            right: '100px',
            padding: '5px 8px',
            minWidth: '300px',
            zIndex: 2,
          }}>
          <Grid2 sx={{ marginBottom: '10px' }}>
            <IconButton
              onClick={() => setOpen(false)}
              sx={{
                position: 'absolute',
                right: 0,
                top: '-2px',
              }}
              size="small"
              variant="contained">
              <HighlightOffRoundedIcon />
            </IconButton>
            <Typography
              variant="h6"
              sx={{ fontSize: '16px', color: 'primary.main' }}>
              <FormattedMessage
                id="SERVICE_PROVIDER_MATCH_LEGEND"
                defaultMessage="Service Provider Match Legend"
              />
            </Typography>
            <Divider />
          </Grid2>

          <Grid2>
            <Stack direction="row" spacing={3}>
              <Stack spacing={1}>
                <LegendBlock
                  item={{
                    name: formatMessage({
                      id: 'LEGEND_SERVICES_INDUSTRY',
                      defaultMessage: 'Services Industry',
                    }),
                    Icon: ServiceIcon,
                    iconProps: {
                      startAngle: 0 + QUADRANT_GAP,
                      endAngle: 90 - QUADRANT_GAP,
                    },
                  }}
                />
                <LegendBlock
                  item={{
                    name: formatMessage({
                      id: 'LEGEND_SERVICES_PRODUCT_LINE',
                      defaultMessage: 'Services Product Line',
                    }),
                    Icon: ServiceIcon,

                    iconProps: {
                      startAngle: 90 + QUADRANT_GAP,
                      endAngle: 180 - QUADRANT_GAP,
                    },
                  }}
                />
                <LegendBlock
                  item={{
                    name: formatMessage({
                      id: 'LEGEND_SERVICES_BRAND',
                      defaultMessage: 'Services Brand',
                    }),
                    Icon: ServiceIcon,
                    iconProps: {
                      startAngle: 180 + QUADRANT_GAP,
                      endAngle: 270 - QUADRANT_GAP,
                    },
                  }}
                />
                <LegendBlock
                  item={{
                    name: formatMessage({
                      id: 'LEGEND_WITHIN_TRAVEL_DISTANCE',
                      defaultMessage: 'Within Service Travel Distance',
                    }),

                    Icon: ServiceIcon,
                    iconProps: {
                      startAngle: 270 + QUADRANT_GAP,
                      endAngle: 0 - QUADRANT_GAP,
                    },
                  }}
                />
              </Stack>
              <Stack spacing={1}>
                <LegendBlock
                  item={{
                    name: formatMessage({
                      id: 'LEGEND_CONDITIONALLY_ACCEPTED_ASSIGNED',
                      defaultMessage: 'Accepted/Assigned',
                    }),
                    Icon: PushPinIcon,
                    iconProps: {
                      sx: {
                        color: 'success.main',
                        transform: 'rotate(45deg)',
                      },
                    },
                  }}
                />
                <LegendBlock
                  item={{
                    name: formatMessage({
                      id: 'LEGEND_CONDITIONALLY_ACCEPTED',
                      defaultMessage: 'Conditionally Accepted',
                    }),
                    Icon: StarIcon,
                    iconProps: { sx: { color: 'success.main' } },
                  }}
                />
                <LegendBlock
                  item={{
                    name: formatMessage({
                      id: 'LEGEND_REJECTED_REVOKED',
                      defaultMessage: 'Rejected/Revoked',
                    }),
                    Icon: BlockIcon,
                    iconProps: { sx: { color: ' #FF0000' } },
                  }}
                />
                <LegendBlock
                  item={{
                    name: formatMessage({
                      id: 'LEGEND_RECEIVED_DISPATCH',
                      defaultMessage: 'Received Dispatch',
                    }),
                    Icon: StarBorderIcon,
                    iconProps: { sx: { color: 'success.main' } },
                  }}
                />
              </Stack>
              <Stack spacing={1}>
                <LegendBlock
                  item={{
                    name: formatMessage({
                      id: 'LEGEND_MISSING_SKILLS',
                      defaultMessage: 'Skills Mismatch',
                    }),
                    Icon: ClearIcon,
                    iconProps: { sx: { color: 'error.main' } },
                  }}
                />
                <LegendBlock
                  item={{
                    name: formatMessage({
                      id: 'LEGEND_NON_COMPLIANT',
                      defaultMessage: 'Non-Compliant',
                    }),
                    Icon: ReceiptLongIcon,
                    iconProps: { sx: { color: 'warning.light' } },
                  }}
                />
                <LegendBlock
                  item={{
                    name: formatMessage({
                      id: 'LEGEND_MANUAL_DISPATCH',
                      defaultMessage: 'Manual Dispatch',
                    }),
                    Icon: PhoneIcon,
                    iconProps: { sx: { color: 'text.secondary' } },
                  }}
                />
                <LegendBlock
                  item={{
                    name: formatMessage({
                      id: 'LEGEND_IN_ACTIVE_FOR_DISPATCH',
                      defaultMessage: 'Inactive for Dispatch',
                    }),
                    Icon: WarningAmberIcon,
                    iconProps: { sx: { color: '#8F5601' } },
                  }}
                />
              </Stack>
            </Stack>
          </Grid2>
        </Card>
      </Slide>
    </div>
  );
}
